/**
 * Bulk Document Search service (Search Results > Documents view).
 *
 * Backend: backend/app/api/api_v1/endpoints/documents_search.py +
 * documents_bulk.py. Paths are relative to the shared axios instance's
 * baseURL (config.API_URL, which already ends in /api/v1), matching every
 * sibling service.
 *
 * Errors are left RAW (rejected axios errors) — call sites must run them
 * through `documentsSearchErrorMessage`, NOT read `err.response.data.detail`
 * directly. These endpoints raise HTTPException with a string `detail` on 400
 * (selection not owned / bad recipient), 413 (size caps), 422 (nothing
 * printable) and 502 (blob/SendGrid failure), but FastAPI's built-in request
 * validation returns `detail` as an ARRAY of `{loc, msg, type}` dicts — the
 * backend registers no RequestValidationError handler, so a malformed body
 * arrives in that shape. `documentsSearchErrorMessage` handles both, plus the
 * blob-bodied errors the zip/print endpoints produce.
 */
import api from './api';
import { describeApiError } from '../utils/apiError';
import { saveAs } from '../utils/saveFile';
import type {
  BulkEmailResponse, BulkItem, BulkSizeResponse,
  DocumentSearchFilters, DocumentSearchResponse,
} from '../types/documentsSearch';

/** What the server could not include, for a "3 file(s) skipped: tif" notice. */
export interface BulkSkipInfo {
  /** X-Skipped-Count — documents left out of the zip/PDF. */
  skipped: number;
  /** X-Skipped-Types — comma-joined file extensions, '' when none/unreported. */
  skippedTypes: string;
}

/**
 * bulkPrint's result. `url` is an object URL for the merged PDF — the CALLER
 * navigates its own pre-opened tab to it (see bulkPrint's doc comment).
 */
export interface BulkPrintResult extends BulkSkipInfo {
  url: string;
}

/**
 * Blob-response endpoints (zip/print) return their ERROR bodies as a Blob too,
 * because responseType applies to every status. Left alone, a 413's JSON
 * `detail` would arrive as an unreadable Blob and the UI would show a generic
 * message. Re-parse it into `error.response.data` so the normal
 * `detail`-reading path works on these endpoints as well.
 */
async function rethrowWithParsedBlobError(error: any): Promise<never> {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      error.response.data = JSON.parse(await data.text());
    } catch {
      // Non-JSON body (proxy HTML, empty response) — leave it as-is; the
      // caller's fallback message covers it.
    }
  }
  throw error;
}

/**
 * Turns any rejection from this service into a message worth showing.
 *
 * Handles the three real shapes: a string `detail` (every HTTPException these
 * endpoints raise), an ARRAY `detail` of pydantic errors (FastAPI request
 * validation — joined on their `msg`), and no/unreadable response (network
 * failure, non-JSON proxy body), which falls through to `describeApiError`'s
 * connectivity wording. Blob error bodies are already re-parsed into objects
 * by `rethrowWithParsedBlobError` before they reach here.
 */
export function documentsSearchErrorMessage(
  err: unknown, fallback = 'The document request failed.',
): string {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  if (data && typeof data === 'object') {
    const detail = (data as { detail?: unknown }).detail;
    if (Array.isArray(detail)) {
      const msgs = detail
        .map((d) => (d && typeof d === 'object' ? (d as { msg?: unknown }).msg : d))
        .filter((m): m is string => typeof m === 'string' && m.length > 0);
      if (msgs.length) return msgs.join('; ');
      return fallback;
    }
  }
  return describeApiError(err, fallback).message;
}

/** `additionalFields` are orders-registry fieldnames the grid added as columns; the
 *  server attaches each row's ORDER values keyed on its pronumber. Omitted from the
 *  body when empty so an unchanged request keeps its old shape. */
export async function searchDocuments(
  pros: number[], filters: DocumentSearchFilters, additionalFields: string[] = [],
): Promise<DocumentSearchResponse> {
  const { data } = await api.post<DocumentSearchResponse>(
    '/documents/search',
    {
      pros,
      ...filters,
      ...(additionalFields.length ? { additional_fields: additionalFields } : {}),
    },
  );
  return data;
}

/**
 * Fetches one result row's blob for inline preview.
 *
 * A plain `<embed src=/api/…>` would skip the Bearer token and 401, so the blob
 * is fetched here and turned into an object URL by the caller. Keyed by the
 * row's composite identity rather than a cms_map recno: the Documents view
 * unions three sources, and only `cms` rows exist in cms_map at all.
 */
export async function previewDocumentBlob(item: BulkItem): Promise<Blob> {
  const { data } = await api.get<Blob>('/documents/preview', {
    params: item,
    responseType: 'blob',
  });
  return data;
}

export async function bulkSize(items: BulkItem[]): Promise<BulkSizeResponse> {
  const { data } = await api.post<BulkSizeResponse>(
    '/documents/bulk/size', { items },
  );
  return data;
}

export async function bulkEmail(
  items: BulkItem[], recipients: string[], note?: string,
): Promise<BulkEmailResponse> {
  const { data } = await api.post<BulkEmailResponse>(
    '/documents/bulk/email', { items, recipients, note },
  );
  return data;
}

/**
 * Prompts for a save location and downloads the selection as a zip. Returns
 * what the server could not retrieve (X-Skipped-Count / X-Skipped-Types) so
 * the caller can warn that the zip is incomplete, or null when the user
 * cancelled the Save As dialog (nothing was fetched or downloaded).
 */
export async function bulkZip(items: BulkItem[]): Promise<BulkSkipInfo | null> {
  let headers: Record<string, unknown> | undefined;
  const saved = await saveAs('cms_documents.zip', async () => {
    const res = await api
      .post('/documents/bulk/zip', { items }, { responseType: 'blob' })
      .catch(rethrowWithParsedBlobError);
    headers = res.headers as Record<string, unknown>;
    return res.data as Blob;
  });
  return saved ? skipInfo(headers) : null;
}

/**
 * Merges the selection into one PDF and returns its object URL — it does NOT
 * open a tab.
 *
 * Popup blockers only allow `window.open` inside the click's own task, and
 * this request takes seconds. So the CALLER opens a placeholder tab
 * synchronously on click with `window.open('', '_blank')`, sets
 * `tab.opener = null` to sever the back-reference, and assigns
 * `tab.location.href = result.url` once this resolves. Do NOT pass 'noopener':
 * it makes `window.open` return null UNCONDITIONALLY, so the blocked branch
 * would always fire. A null tab therefore means the popup really was blocked —
 * offer the URL manually (see SearchDocumentsView.handlePrint).
 *
 * The object URL is released on a 60s timer (house pattern) — long enough for
 * the tab to finish loading it, short enough not to leak the merged PDF for
 * the life of the document. A manual-open link must warn or re-request past
 * that window: after 60s the URL is revoked and the click does nothing.
 */
export async function bulkPrint(items: BulkItem[]): Promise<BulkPrintResult> {
  const res = await api
    .post('/documents/bulk/print', { items }, { responseType: 'blob' })
    .catch(rethrowWithParsedBlobError);
  // No Blob re-wrap: responseType 'blob' already yields a Blob typed from the
  // response's Content-Type (application/pdf).
  const url = URL.createObjectURL(res.data as Blob);
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return { url, ...skipInfo(res.headers) };
}

/**
 * Reads the X-Skipped-Count / X-Skipped-Types response headers. Axios
 * lowercases header keys. Same-origin in every deployment (the API is proxied
 * under the app's own host), so no CORS Access-Control-Expose-Headers concern
 * applies; if the API ever moves cross-origin, the backend must expose both
 * headers explicitly or this silently reads 0 / ''.
 */
export function skipInfo(headers: Record<string, unknown> | undefined): BulkSkipInfo {
  const raw = headers?.['x-skipped-types'];
  return {
    skipped: skippedCount(headers),
    skippedTypes: typeof raw === 'string' ? raw : '',
  };
}

export function skippedCount(headers: Record<string, unknown> | undefined): number {
  const raw = headers?.['x-skipped-count'];
  const n = Number(raw);
  // Number(undefined) is NaN and Number(null)/Number('') is 0 — both mean
  // "not reported", so require an actual numeric header value.
  return raw !== undefined && raw !== null && raw !== '' && Number.isFinite(n) ? n : 0;
}
